import React, { useState, FC } from 'react'
import { Trans, t } from '@lingui/macro'
import { Modal } from '../../../../../components/common/modal/Modal'
import {
  ButtonPrimary,
  ButtonClean,
  ButtonOutline
} from 'front-end/src/components'

import Input from '../../../../../components/common/form/input/Input'
import DatePicker from '../../../../../components/common/date-time-pickers/DatePicker'

import './CustomMetricModal.less'
import IconButton from '../../../../../components/common/form/button/IconButton'
import IconBan from '../../../../../components/common/icon/IconBan'
import { notification } from '../../../../../components/common/notifications/Notifications'
import IconPlus2 from '../../../../../components/common/icon/IconPlus2'
import IconGraph from '../../../../../components/common/icon/IconGraph'
import { usePatchDashboardMutation } from '../../../DashboardDrawerWithGearButton.codegen'
import {
  DashboardSummaryQuery,
  DashboardSummaryQueryVariables
} from '../../DashboardSummary.codegen'
import {
  useAddCustomMetricMutation,
  useRemoveCustomMetricMutation
} from './CustomMetricModal.codegen'
import { formatDateShort } from 'front-end/src/utils/dateHelpers'
import dayjs from 'shared/dayjs'

interface IProps {
  dashboard: NonNullable<
    NonNullable<DashboardSummaryQuery['admin']>['dashboard']
  >
  refetch: (
    variables?: DashboardSummaryQueryVariables | undefined
  ) => Promise<any>
  onHide: () => void
}

export const CustomMetricModal: FC<IProps> = ({
  dashboard,
  refetch,
  onHide
}) => {
  const [date, setDate] = useState(dayjs().startOf('day').toISOString())
  const [value, setValue] = useState<string>('')
  const [name, setName] = useState(dashboard.metric_name || 'Custom metric')
  const [targetValue, setTargetValue] = useState(
    dashboard.metric_target_value?.toString() || ''
  )
  const [addCustomMetricFn] = useAddCustomMetricMutation()
  const [removeCustomMetricFn] = useRemoveCustomMetricMutation()
  const [patchDashboardFn] = usePatchDashboardMutation()

  return (
    <Modal
      show={true}
      className="CustomMetric"
      onHide={onHide}
      header={
        <>
          <IconGraph />
          <span>
            <Trans>Custom metric</Trans>
          </span>
        </>
      }
      controls={[
        <ButtonClean key={1} onClick={onHide}>
          <Trans>Cancel</Trans>
        </ButtonClean>,
        <ButtonPrimary
          key={2}
          className="Button__next"
          onClick={async () => {
            await patchDashboardFn({
              variables: {
                id: dashboard.id,
                data: {
                  metric_name: name,
                  metric_target_value: parseFloat(targetValue)
                }
              }
            })
            await refetch()

            onHide()
          }}
        >
          <Trans>Done</Trans>
        </ButtonPrimary>
      ]}
    >
      <div className="CustomMetric__fields">
        <div className="CustomMetric__fields__row">
          <div className="CustomMetric__fields__label">
            <Trans>Metric name</Trans>
          </div>
          <Input
            className="CustomMetric__fields__metricName"
            value={name}
            onChange={(value) => setName(value)}
          />
        </div>
        <div className="CustomMetric__fields__row">
          <div className="CustomMetric__fields__label">
            <Trans>Target value</Trans>
          </div>
          <Input
            className="CustomMetric__fields__targetValue"
            type="number"
            step="0.01"
            value={targetValue}
            onChange={(value) => setTargetValue(value)}
          />
        </div>
      </div>

      <div className="CustomMetric__container">
        <div className="CustomMetric__container__header">
          <Trans>Data table</Trans>
        </div>

        <table className="CustomMetric__table">
          <thead>
            <tr>
              <th>
                <Trans>Date</Trans>
              </th>
              <th>
                <Trans>Value</Trans>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {!dashboard.customMetricEntries?.length && (
              <>
                <tr>
                  <td colSpan={3}>&nbsp;</td>
                </tr>
              </>
            )}
            {dashboard.customMetricEntries?.map((entry, i) => (
              <tr key={i}>
                <td>{formatDateShort(entry.timestamp)}</td>
                <td>{entry.value}</td>
                <td className="CustomMetric__table__remove">
                  <IconButton
                    icon={<IconBan />}
                    hint={t`Remove`}
                    onClick={async () => {
                      await removeCustomMetricFn({
                        variables: {
                          dashboardId: dashboard.id,
                          id: entry.id
                        }
                      })

                      refetch()
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="CustomMetric__entry">
        <DatePicker
          className="from"
          value={dayjs(date).format('L')}
          onChange={(value) => {
            setDate(dayjs(value.toString()).startOf('day').toISOString())
          }}
        />
        <Input
          type="number"
          value={value}
          step="0.001"
          placeholder="Value"
          onChange={(value) => setValue(value)}
        />
        <ButtonOutline
          onClick={async () => {
            if (value.length === 0) {
              notification.warning(t`Value can't be empty`)
            } else {
              await addCustomMetricFn({
                variables: {
                  dashboardId: dashboard.id,
                  timestamp: date,
                  value: parseFloat(value)
                }
              })
              setValue('')
              refetch()
            }
          }}
          leftIcon={<IconPlus2 />}
        >
          <Trans>Add row</Trans>
        </ButtonOutline>
      </div>
    </Modal>
  )
}
